import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { Button, FormInput, FormTextarea, SearchableSelect } from '../../../components/ui';

const STEPS = [
  "Basic Client Information",
  "Site Visit Details",
  "Property Measurements",
  "Room-wise Measurement Section",
  "Wall Measurements",
  "Door Details",
  "Window Details",
  "Electrical Points",
  "Plumbing Points",
  "Ceiling Details",
  "Flooring Details",
  "Kitchen-Specific Measurements",
  "Wardrobe Measurements",
  "Site Condition Checklist",
  "Photo Upload Section",
  "Site Issues / Challenges",
  "Client Requirements Section",
  "Competitor Information",
  "Budget & Conversion Notes",
  "Designer Notes",
  "Measurement Drawing Upload",
  "Digital Signature"
];

export const SiteVisitWizard = ({ initialData = {}, onSubmit, onCancel, loading = false, account, fullName, commonNotes }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({ ...initialData, files: [], drawings: [] });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    onSubmit(formData);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="grid grid-cols-2 gap-4">
            <SearchableSelect
              label="Property Type"
              value={formData.propertyType || ""}
              onChange={value => setFormData({ ...formData, propertyType: value })}
              placeholder="Search or Select"
              readOnly={!!account?.propertyType}
              options={["Apartment", "Villa", "Independent House", "Office", "Commercial Shop", "Restaurant", "Cafe"]}
            />
            <FormInput
              label="Property Address"
              value={formData.propertyAddress || ""}
              onChange={e => setFormData({ ...formData, propertyAddress: e.target.value })}
              placeholder="Enter full address"
            />
            <SearchableSelect
              label="Property Size"
              value={formData.propertySize || ""}
              onChange={value => setFormData({ ...formData, propertySize: value })}
              placeholder="Search or Select"
              readOnly={!!account?.propertySize}
              options={["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Duplex", "Studio", "Office 1000 sqft"]}
            />
            <SearchableSelect
              label="Project Type"
              value={formData.projectType || ""}
              onChange={value => setFormData({ ...formData, projectType: value })}
              placeholder="Search or Select"
              readOnly={!!account?.projectType}
              options={["Full Home Interior", "Modular Kitchen", "Wardrobe", "Bedroom Interior", "Living Room", "TV Unit", "False Ceiling", "Office Interior", "Renovation", "Custom Work"]}
            />
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Visit Date" type="date" name="visitDate" value={formData.visitDate || ''} onChange={handleChange} />
            <FormInput label="Assigned Designer" name="inspectorName" value={formData.inspectorName || ''} onChange={handleChange} />
          </div>
        );
      case 3:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Total Area (Sqft)" type="number" name="totalArea" value={formData.totalArea || ''} onChange={handleChange} />
            <FormInput label="Carpet Area (Sqft)" type="number" name="carpetArea" value={formData.carpetArea || ''} onChange={handleChange} />
          </div>
        );
      case 4:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Number of Rooms" type="number" name="roomCount" value={formData.roomCount || ''} onChange={handleChange} />
            <FormInput label="Room Types (e.g. 2 Beds, 1 Hall)" name="roomTypes" value={formData.roomTypes || ''} onChange={handleChange} />
          </div>
        );
      case 5:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Average Wall Length (ft)" type="number" name="wallLength" value={formData.wallLength || ''} onChange={handleChange} />
            <FormInput label="Average Wall Height (ft)" type="number" name="wallHeight" value={formData.wallHeight || ''} onChange={handleChange} />
          </div>
        );
      case 6:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Number of Doors" type="number" name="doorCount" value={formData.doorCount || ''} onChange={handleChange} />
            <FormInput label="Door Material" name="doorType" value={formData.doorType || ''} onChange={handleChange} />
          </div>
        );
      case 7:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Number of Windows" type="number" name="windowCount" value={formData.windowCount || ''} onChange={handleChange} />
            <FormInput label="Window Frame Type" name="windowType" value={formData.windowType || ''} onChange={handleChange} />
          </div>
        );
      case 8:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Total Switchboards" type="number" name="switchboards" value={formData.switchboards || ''} onChange={handleChange} />
            <FormInput label="Relocation Needed?" name="relocationNeeded" placeholder="Yes/No" value={formData.relocationNeeded || ''} onChange={handleChange} />
          </div>
        );
      case 9:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Pipe Condition" name="pipeCondition" value={formData.pipeCondition || ''} onChange={handleChange} />
            <FormInput label="Alteration Needed?" name="plumbingAlteration" placeholder="Yes/No" value={formData.plumbingAlteration || ''} onChange={handleChange} />
          </div>
        );
      case 10:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Ceiling Height (ft)" type="number" name="ceilingHeight" value={formData.ceilingHeight || ''} onChange={handleChange} />
            <FormInput label="False Ceiling?" name="falseCeiling" placeholder="Yes/No" value={formData.falseCeiling || ''} onChange={handleChange} />
          </div>
        );
      case 11:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Existing Flooring" name="existingFlooring" value={formData.existingFlooring || ''} onChange={handleChange} />
            <FormInput label="Planned Flooring" name="plannedFlooring" value={formData.plannedFlooring || ''} onChange={handleChange} />
          </div>
        );
      case 12:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Counter Length (ft)" type="number" name="counterLength" value={formData.counterLength || ''} onChange={handleChange} />
            <FormInput label="Sink Location" name="sinkLocation" value={formData.sinkLocation || ''} onChange={handleChange} />
          </div>
        );
      case 13:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Wardrobe Width (ft)" type="number" name="wardrobeWidth" value={formData.wardrobeWidth || ''} onChange={handleChange} />
            <FormInput label="Wardrobe Height (ft)" type="number" name="wardrobeHeight" value={formData.wardrobeHeight || ''} onChange={handleChange} />
          </div>
        );
      case 14:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Seepage/Dampness?" name="seepage" placeholder="Yes/No" value={formData.seepage || ''} onChange={handleChange} />
            <FormInput label="Wall Cracks?" name="wallCracks" placeholder="Yes/No" value={formData.wallCracks || ''} onChange={handleChange} />
          </div>
        );
      case 15:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 mb-2">Please upload site photos.</p>
            <div className="p-8 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center text-slate-400">
               Photo Upload Component Placeholder
            </div>
          </div>
        );
      case 16:
        return (
          <div className="grid grid-cols-1 gap-4">
            <FormTextarea label="Material Access Issues?" name="accessIssues" rows={2} value={formData.accessIssues || ''} onChange={handleChange} />
            <FormTextarea label="Working Hour Restrictions?" name="hourRestrictions" rows={2} value={formData.hourRestrictions || ''} onChange={handleChange} />
          </div>
        );
      case 17:
        return (
          <div className="grid grid-cols-1 gap-4">
            <FormInput label="Design Style Preference" name="stylePreference" placeholder="e.g. Modern, Minimalist" value={formData.stylePreference || ''} onChange={handleChange} />
            <FormTextarea label="Core Needs" name="coreNeeds" rows={2} value={formData.coreNeeds || ''} onChange={handleChange} />
          </div>
        );
      case 18:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Competitor Name" name="competitorName" value={formData.competitorName || ''} onChange={handleChange} />
            <FormInput label="Competitor Quote (₹)" type="number" name="competitorQuote" value={formData.competitorQuote || ''} onChange={handleChange} />
          </div>
        );
      case 19:
        return (
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Estimated Budget (₹)" type="number" name="estimatedBudget" value={formData.estimatedBudget || ''} onChange={handleChange} />
            <FormInput label="Probability (%)" type="number" name="conversionProb" value={formData.conversionProb || ''} onChange={handleChange} />
          </div>
        );
      case 20:
        return (
          <div className="grid grid-cols-1 gap-4">
            <FormTextarea label="Design Ideas" name="designIdeas" rows={2} value={formData.designIdeas || ''} onChange={handleChange} />
            <FormTextarea label="Recommendations" name="recommendations" rows={2} value={formData.recommendations || ''} onChange={handleChange} />
          </div>
        );
      case 21:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 mb-2">Upload Measurement Drawings/Plans.</p>
            <div className="p-8 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center text-slate-400">
               Drawing Upload Placeholder
            </div>
          </div>
        );
      case 22:
        return (
          <div className="space-y-4">
             <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
               <p className="text-sm text-slate-700 mb-3 font-medium">I confirm all details are accurate.</p>
               <FormInput label="Client/Inspector Name" name="signatureName" value={formData.signatureName || ''} onChange={handleChange} />
             </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4 h-[280px] overflow-y-auto pr-1">
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
          <Info size={16} className="flex-shrink-0 mt-0.5 text-slate-500" />
          <div className="flex flex-col min-w-0 w-full">
            <div className="text-sm font-semibold text-slate-800">
              Update Status to Site Visit - {STEPS[currentStep - 1]} ({currentStep}/22)
            </div>
            <div className="text-[10px] text-slate-600 mt-1 truncate">
              {fullName} / {account?.phoneNumber || account?.phone || "N/A"} / {account?.email || "N/A"} {account?.propertyType && `/ ${account.propertyType}`}
            </div>
          </div>
        </div>

        {renderStepContent()}
        {commonNotes}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          onClick={handlePrev}
          disabled={currentStep === 1}
          variant="secondary"
          className="px-6"
        >
          Previous
        </Button>
        
        {currentStep < STEPS.length ? (
          <Button
            type="button"
            onClick={handleNext}
            className="flex-1"
            variant="orange"
          >
            Next
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            loading={loading}
            className="flex-1"
            variant="orange"
          >
            Confirm Site Visit
          </Button>
        )}
      </div>
    </div>
  );
};
